/** Operator-console (apps/admin) English strings — `admin` i18n namespace. */
export default {
  nav: {
    users: 'Users',
    accounts: 'Accounts',
    transactions: 'Transactions',
    fx_conversions: 'FX conversions',
    reviews: 'Reviews',
    risk_assessments: 'Risk assessments',
    security: 'Security',
    kyc: 'KYC reviews',
    audit_logs: 'Audit logs',
    webhooks: 'Webhooks',
    business_model: 'Business model',
    sign_out: 'Sign out',
  },
  login: {
    title: 'Operator console',
    subtitle: 'Sign in to continue.',
    email_label: 'Email address',
    password_label: 'Password',
    submit: 'Sign in',
    twofa_code_label: 'Authentication code',
    twofa_submit: 'Verify',
    not_authorized: "This account doesn't have operator access.",
  },
  common: {
    loading: 'Loading…',
    none: '—',
    page: 'Page {{page}}',
    prev: 'Previous',
    next: 'Next',
    search: 'Search',
    filter: 'Filter',
    all: 'All',
    back: 'Back',
    refresh: 'Refresh',
  },
  users: {
    title: 'Users',
    search_placeholder: 'Search by email…',
    status_filter: 'Status',
    col_email: 'Email',
    col_status: 'Status',
    col_plan: 'Plan',
    col_kyc: 'KYC',
    col_created: 'Created',
    empty: 'No users match.',
  },
  user_detail: {
    title: 'User detail',
    section_profile: 'Profile',
    section_actions: 'Actions',
    section_audit: 'Audit log',
    email: 'Email',
    status: 'Status',
    plan: 'Plan',
    kyc: 'KYC status',
    role: 'Role',
    created: 'Created',
    suspend: 'Suspend account',
    suspend_reason: 'Reason (internal)',
    reactivate: 'Reactivate',
    force_logout: 'Force global sign-out',
    audit_empty: 'No audit entries.',
    col_action: 'Action',
    col_time: 'Time',
  },
  statuses: {
    pending_email: 'Pending email',
    pending_kyc: 'Pending KYC',
    active: 'Active',
    review: 'Under review',
    suspended: 'Suspended',
    closed: 'Closed',
  },
  kyc: {
    title: 'KYC reviews',
    subtitle: 'Verification stays with Sumsub — this is read-only oversight.',
    col_user: 'User',
    col_tier: 'Tier',
    col_status: 'Status',
    col_created: 'Started',
    empty: 'No pending reviews.',
  },
  audit: {
    title: 'Audit logs',
    filter_action: 'Action contains…',
    filter_resource: 'Resource type',
    col_action: 'Action',
    col_actor: 'Actor',
    col_resource: 'Resource',
    col_time: 'Time',
    empty: 'No matching entries.',
  },
  webhooks: {
    title: 'Webhooks',
    col_source: 'Source',
    col_type: 'Type',
    col_valid: 'Signature',
    col_processed: 'Processed',
    col_created: 'Received',
    empty: 'No webhooks received.',
    valid_yes: 'Valid',
    valid_no: 'Invalid',
  },
  accounts: {
    title: 'Accounts',
    status_filter: 'Status',
    col_owner: 'Owner',
    col_iban: 'IBAN',
    col_status: 'Status',
    col_balance: 'Balance',
    col_created: 'Opened',
    empty: 'No accounts match.',
  },
  account_statuses: {
    opening: 'Opening',
    opened: 'Opened',
    review: 'Under review',
    suspended: 'Suspended',
    closed: 'Closed',
  },
  transactions: {
    title: 'Transactions',
    search_placeholder: 'Search name, IBAN, reference…',
    status_filter: 'Status',
    col_owner: 'Owner',
    col_type: 'Type',
    col_amount: 'Amount',
    col_status: 'Status',
    col_date: 'Date',
    empty: 'No transactions match.',
  },
  reviews: {
    title: 'Reviews',
    queue_title: 'Review queue',
    queue_subtitle: 'Oldest first. Resolve or assign reviews to yourself.',
    empty: 'No open reviews.',
    started: 'Started',
    escalated: 'Escalated (over 24h)',
    resolve: 'Resolve',
    assign_me: 'Assign to me',
  },
  review_types: {
    kyc_refresh: 'Identity refresh',
    transaction_review: 'Transaction review',
    aml_check: 'Compliance check',
    fraud_check: 'Fraud check',
    other: 'Other',
  },
  review_statuses: {
    in_progress: 'In progress',
    resolved: 'Resolved',
    escalated: 'Escalated',
  },
  security: {
    title: 'Security events',
    subtitle: 'Detection signal — failed-login bursts, invalid webhook signatures, anomalies.',
    severity_filter: 'Severity',
    col_time: 'Time',
    col_severity: 'Severity',
    col_event: 'Event',
    col_ip: 'IP',
    col_user: 'User',
    empty: 'No security events.',
  },
  severities: {
    info: 'Info',
    warning: 'Warning',
    high: 'High',
    critical: 'Critical',
  },
  risk: {
    title: 'Risk assessments',
    subtitle: 'Escalated pre-flight checks. Flags are ops-only — never shared with the customer.',
    empty: 'No escalated assessments.',
    score: 'Score {{score}}/100',
    justification: 'Customer justification',
    view_doc: 'View document',
    created: 'Created',
    reason_placeholder: 'Decision reason (internal, audited)',
    approve: 'Approve',
    reject: 'Reject',
    flags: {
      unusual_amount: 'Unusual amount',
      sensitive_country: 'Sensitive destination',
      new_beneficiary: 'New beneficiary',
      high_frequency: 'High frequency',
      round_amount_structuring: 'Round-amount structuring',
      just_below_threshold: 'Just below threshold',
      peak_unusual_time: 'Unusual hour',
    },
  },
  fx: {
    title: 'FX conversions',
    subtitle: 'Intra-EU conversions — mid-market vs applied rate and the declared margin.',
    status_filter: 'Status',
    col_user: 'User',
    col_from: 'From',
    col_to: 'To',
    col_mid_rate: 'Mid-market',
    col_applied_rate: 'Applied',
    col_margin: 'Margin',
    col_status: 'Status',
    col_date: 'Date',
    empty: 'No conversions match.',
  },
  business_model: {
    title: 'Business model figures',
    subtitle: 'Monthly revenue shown on the public page. Publishing is audited.',
    form_title: 'Add or update a month',
    period: 'Period (YYYY-MM)',
    subscriptions: 'Subscriptions (€)',
    interchange: 'Card interchange (€)',
    fx_margin: 'FX margin (€)',
    paylink: 'Pay-by-link (€)',
    notes: 'Notes (internal)',
    save: 'Save figures',
    list_title: 'Published & draft months',
    col_period: 'Period',
    col_total: 'Total',
    col_status: 'Status',
    col_actions: 'Actions',
    published: 'Published',
    draft: 'Draft',
    edit: 'Edit',
    publish: 'Publish',
    unpublish: 'Unpublish',
    empty: 'No figures yet.',
    updated_hint: 'Last updated {{date}}.',
  },
} as const
